import React, { useEffect, useRef, useState, useId } from "react";
import mermaid from "mermaid";
import { AlertTriangle, Code2, Eye, EyeOff } from "lucide-react";

// One-time global init — dark theme tuned to the BBH palette.
let mermaidReady = false;
const ensureMermaid = () => {
  if (mermaidReady) return;
  mermaid.initialize({
    startOnLoad: false,
    securityLevel: "loose",
    theme: "base",
    fontFamily:
      'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial',
    themeVariables: {
      background: "#0a0a0a",
      // Primary palette
      primaryColor: "#0f1a14",
      primaryTextColor: "#e4e4e7",
      primaryBorderColor: "#10b981",
      // Flowchart-specific
      mainBkg: "#0f1a14",
      nodeBkg: "#0f1a14",
      nodeBorder: "#10b981",
      nodeTextColor: "#e4e4e7",
      clusterBkg: "rgba(16,185,129,0.04)",
      clusterBorder: "#3f3f46",
      defaultLinkColor: "#52525b",
      edgeLabelBackground: "#0a0a0a",
      // Generic
      lineColor: "#52525b",
      secondaryColor: "#1c1917",
      secondaryBorderColor: "#3f3f46",
      secondaryTextColor: "#e4e4e7",
      tertiaryColor: "#0f1a14",
      tertiaryBorderColor: "#10b981",
      tertiaryTextColor: "#e4e4e7",
      noteBkgColor: "#1c1917",
      noteTextColor: "#fafafa",
      titleColor: "#d4af37",
      // Quadrant chart
      quadrant1Fill: "#0f1a14",
      quadrant2Fill: "#1a140f",
      quadrant3Fill: "#0a0a0a",
      quadrant4Fill: "#14140f",
      quadrant1TextFill: "#10b981",
      quadrant2TextFill: "#d4af37",
      quadrant3TextFill: "#71717a",
      quadrant4TextFill: "#fbbf24",
      quadrantTitleFill: "#fafafa",
      quadrantXAxisTextFill: "#a1a1aa",
      quadrantYAxisTextFill: "#a1a1aa",
      quadrantPointFill: "#10b981",
      quadrantPointTextFill: "#fafafa",
      // Pie
      pie1: "#10b981",
      pie2: "#d4af37",
      pie3: "#fb7185",
      pie4: "#60a5fa",
      pie5: "#a78bfa",
    },
    flowchart: { useMaxWidth: false, htmlLabels: true, nodeSpacing: 50, rankSpacing: 60, padding: 8 },
    sequence: { useMaxWidth: false },
    gantt: { useMaxWidth: false },
    er: { useMaxWidth: false },
    journey: { useMaxWidth: false },
    fontSize: 16,
  });
  mermaidReady = true;
};

/**
 * After mermaid renders, walk the SVG once and inline-set the fill / stroke
 * on every shape element. This wins every CSS specificity battle with
 * mermaid's per-svg <style> block (which can use !important).
 */
const enforceDarkPalette = (svgString) => {
  if (!svgString || typeof window === "undefined") return svgString;
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgString, "image/svg+xml");
    const svg = doc.documentElement;
    if (!svg || svg.nodeName.toLowerCase() !== "svg") return svgString;

    const NODE_FILL = "#0f1a14";
    const NODE_STROKE = "#10b981";
    const EDGE_STROKE = "#52525b";
    const TEXT_COLOR = "#e4e4e7";

    // Shape elements that are part of a node container: force dark fill
    svg.querySelectorAll(
      ".node rect, .node polygon, .node circle, .node ellipse, .node path"
    ).forEach((el) => {
      // Don't repaint elements that ARE the edge arrow markers
      if (el.closest("marker")) return;
      el.setAttribute("fill", NODE_FILL);
      el.setAttribute("stroke", NODE_STROKE);
      el.style.fill = NODE_FILL;
      el.style.stroke = NODE_STROKE;
    });
    // Cluster (subgraph) containers
    svg.querySelectorAll(".cluster rect").forEach((el) => {
      el.setAttribute("fill", "rgba(16,185,129,0.04)");
      el.setAttribute("stroke", "#3f3f46");
      el.style.fill = "rgba(16,185,129,0.04)";
      el.style.stroke = "#3f3f46";
    });
    // Edge paths: dim stroke, no fill
    svg.querySelectorAll("path.flowchart-link, path.edge, path.relation").forEach((el) => {
      el.setAttribute("stroke", EDGE_STROKE);
      el.setAttribute("fill", "none");
      el.style.stroke = EDGE_STROKE;
      el.style.fill = "none";
    });
    // Text + labels: light foreground
    svg.querySelectorAll("text, tspan").forEach((el) => {
      el.setAttribute("fill", TEXT_COLOR);
      el.style.fill = TEXT_COLOR;
    });
    svg.querySelectorAll("foreignObject div, foreignObject span, foreignObject p, .nodeLabel, .edgeLabel, .label").forEach((el) => {
      el.style.color = TEXT_COLOR;
      el.style.background = "transparent";
    });
    // Mermaid sets inline `style="max-width: NNNpx; ..."` on the <svg> root,
    // which clamps it back to a smaller size even when useMaxWidth is false.
    // Strip those clamps so the diagram renders at its natural pixel size and
    // the surrounding container handles overflow.
    svg.style.maxWidth = "none";
    svg.style.height = "auto";
    svg.removeAttribute("width");
    // Keep the explicit width attribute if mermaid set it via viewBox; many
    // diagrams now render at full intrinsic resolution.
    return new XMLSerializer().serializeToString(svg);
  } catch {
    return svgString;
  }
};

/**
 * Strip characters the Mermaid parser frequently chokes on. We preserve
 * them in the "show source" fallback so nothing is lost.
 *
 *   - Emoji + miscellaneous-symbols break quadrantChart axis labels.
 *   - Unicode arrows (→ ⟶ ⇒) — replaced with ASCII "->".
 *   - Smart quotes — replaced with straight quotes.
 */
const sanitizeMermaid = (raw) => {
  if (!raw) return raw;
  let out = raw;
  // Remove emoji-range codepoints (rough but effective)
  out = out.replace(
    /[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}\u{2B00}-\u{2BFF}]/gu,
    ""
  );
  // Unicode arrows → ASCII
  out = out
    .replace(/[\u2192\u27F6\u2794\u279C\u2799\u21D2]/g, "->")
    .replace(/[\u2190\u27F5\u21D0]/g, "<-");
  // Smart quotes
  out = out
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/[\u2018\u2019]/g, "'");

  // xychart-beta safety net: if the LLM emits `y-axis "Label"` without the
  // mandatory `min --> max` range, mermaid throws "Syntax error in text".
  // Auto-append a `0 --> <ceil(maxBar * 1.15)>` range computed from the
  // bar line's largest value so the chart renders instead of failing.
  if (/^\s*xychart-beta\b/m.test(out)) {
    const barLine = out.match(/^\s*bar\s*\[([^\]]+)\]/m);
    let maxVal = 0;
    if (barLine) {
      barLine[1]
        .split(",")
        .map((s) => parseFloat(String(s).replace(/[^0-9.\-]/g, "")))
        .filter((n) => !isNaN(n))
        .forEach((n) => {
          if (n > maxVal) maxVal = n;
        });
    }
    const yMax = maxVal > 0 ? Math.ceil((maxVal * 1.15) / 100) * 100 : 100;
    out = out.replace(
      /^(\s*y-axis\s+"[^"]*")\s*$/m,
      (_m, prefix) => `${prefix} 0 --> ${yMax}`
    );
  }

  return out;
};

export default function MermaidBlock({ code }) {
  const [svg, setSvg] = useState(null);
  const [error, setError] = useState(null);
  const [showSource, setShowSource] = useState(false);
  const id = useId().replace(/[^a-zA-Z0-9]/g, "");
  const cancelled = useRef(false);

  useEffect(() => {
    cancelled.current = false;
    if (!code || !code.trim()) {
      setSvg(null);
      setError(null);
      return;
    }
    // Debounce: while the code prop keeps changing (mid-stream), skip the
    // parse+render so mermaid never trips on partial fences and leaves stray
    // "Syntax error" nodes in the DOM. Once the code stabilises for ~250ms,
    // render once. This eliminates the syntax-error flash that only cleared
    // on page reload.
    const debounce = setTimeout(() => {
      if (cancelled.current) return;
      // Clear previous state only when we're actually about to render, so a
      // successful chart stays visible while a follow-up stream is happening.
      setError(null);
      const cleaned = sanitizeMermaid(code);
      (async () => {
        try {
          ensureMermaid();
          await mermaid.parse(cleaned, { suppressErrors: false });
          const { svg } = await mermaid.render(`m-${id}`, cleaned);
          if (!cancelled.current) setSvg(enforceDarkPalette(svg));
        } catch (e) {
          if (!cancelled.current) {
            setSvg(null);
            const msg = (e?.message || String(e || "Diagram could not be rendered")).split("\n")[0];
            setError(msg.length > 220 ? msg.slice(0, 220) + "…" : msg);
          }
        }
      })();
    }, 250);
    return () => {
      cancelled.current = true;
      clearTimeout(debounce);
    };
  }, [code, id]);

  if (error) {
    return (
      <div className="my-3 rounded-xl border border-amber-500/25 bg-amber-500/5 p-4">
        <div className="flex items-start gap-2.5">
          <AlertTriangle className="w-4 h-4 text-amber-300 mt-0.5 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-[11px] uppercase tracking-[0.22em] text-amber-300/90 font-semibold">
              Diagram couldn't render
            </div>
            <div className="text-xs text-zinc-400 mt-1">
              The agent's diagram had a syntax issue and is shown as code
              below. The written explanation above already covers the
              same idea.
            </div>
            <button
              onClick={() => setShowSource((s) => !s)}
              className="mt-2 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-zinc-400 hover:text-amber-300"
            >
              {showSource ? <EyeOff className="w-3 h-3" /> : <Code2 className="w-3 h-3" />}
              {showSource ? "Hide source" : "Show source"}
            </button>
            {showSource && (
              <pre className="mt-3 rounded-lg bg-zinc-950 border border-zinc-800 p-3 overflow-x-auto text-[12px]">
                <code className="font-mono text-zinc-300">{code}</code>
              </pre>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!svg) {
    return (
      <div className="my-3 rounded-xl border border-zinc-800 bg-zinc-950/40 p-6 flex items-center justify-center text-xs text-zinc-500 gap-2">
        <span className="typing-dot" />
        <span className="typing-dot" />
        <span className="typing-dot" />
        <span className="ml-2">Rendering diagram…</span>
      </div>
    );
  }

  return (
    <ZoomableMedia label="Mermaid diagram">
      <div
        className="mermaid-svg my-3 rounded-xl border border-zinc-800 bg-zinc-950/40 p-4 overflow-x-auto overflow-y-hidden"
        style={{ maxWidth: "100%" }}
        // mermaid runs its own DOMPurify pass internally (securityLevel:
        // "strict") on the label HTML. We trust that output verbatim here
        // — applying a second sanitization pass would strip legitimate
        // <foreignObject>/<tspan> structures and leave empty boxes.
        dangerouslySetInnerHTML={{ __html: svg }}
      />
    </ZoomableMedia>
  );
}

/**
 * Wrap any media (svg/img/table) in a div that emits a custom "zoom-media"
 * event on click. The global ZoomModal at the App root listens for that
 * event and renders a portal modal with the cloned content.
 */
export const ZoomableMedia = ({ children, label = "Open in zoom view" }) => {
  const ref = useRef(null);
  const onClick = (e) => {
    // Don't hijack link/button clicks
    if (e.target.closest("a, button, [data-no-zoom]")) return;
    const html = ref.current?.innerHTML;
    if (!html) return;
    window.dispatchEvent(
      new CustomEvent("zoom-media", { detail: { html, label } })
    );
  };
  return (
    <div
      ref={ref}
      onClick={onClick}
      className="zoomable cursor-zoom-in transition-transform duration-200 hover:scale-[1.01]"
      title={label}
      data-zoomable="true"
    >
      {children}
    </div>
  );
};
